import { JobsiteUpdateInput } from 'apollo/generated/client-operations';
import {
  FormInputTypes,
  getUpdateInputValueFunction,
  GridColSpan,
  TypedFormInputs,
} from 'components/form';
import { EditJobsiteConfigurationFormData } from 'containers/jobsiteConfiguration/types';
import moment from 'moment';
import { DeepMap } from 'react-hook-form';
import { ensureNonEmptyItems, stringifyEmptyFields } from 'utils';
import { isValidEmailAddress } from 'utils/validation';
import { Jobsite } from './types';

const emailListValidator = (listInput: string): string => {
  if (!listInput?.trim()?.length) return null;
  const items = listInput.trim().split(',');
  // eslint-disable-next-line no-restricted-syntax
  for (const email of items) {
    if (!email?.trim()?.length) return 'Blank emails not allowed in a list.';
    if (!isValidEmailAddress(email.trim()))
      return `Invalid email address [${email.trim()}]`;
  }
  return null;
};

export const notificationsSectionInputs: TypedFormInputs<
  EditJobsiteConfigurationFormData['notifications']
> = {
  hardwareStatusChange: {
    element: FormInputTypes.TextAreaField,
    label: 'Hardware Status Changes',
    layout: [GridColSpan.SpanFull],
    elementProps: { placeholder: 'Enter email addresses, separated by comma' },
    validation: { validate: emailListValidator },
  },
  bansAndSuspensions: {
    element: FormInputTypes.TextAreaField,
    label: 'Bans & Suspensions',
    layout: [GridColSpan.SpanFull],
    elementProps: { placeholder: 'Enter email addresses, separated by comma' },
    validation: { validate: emailListValidator },
  },
  escortPrivilegeChanges: {
    element: FormInputTypes.TextAreaField,
    label: 'Escort Privilege Changes',
    layout: [GridColSpan.SpanFull],
    elementProps: { placeholder: 'Enter email addresses, separated by comma' },
    validation: { validate: emailListValidator },
  },
  jobsiteAssignmentChanges: {
    element: FormInputTypes.TextAreaField,
    label: 'Jobsite Assignment Changes',
    layout: [GridColSpan.SpanFull],
    elementProps: { placeholder: 'Enter email addresses, separated by comma' },
    validation: { validate: emailListValidator },
  },
  rejectedSwipes: {
    element: FormInputTypes.TextAreaField,
    label: 'Access Denials',
    layout: [GridColSpan.SpanFull],
    elementProps: { placeholder: 'Enter email addresses, separated by comma' },
    validation: { validate: emailListValidator },
  },
  dailyAccessReport: {
    element: FormInputTypes.TextAreaField,
    label: 'Daily Access Report',
    layout: [GridColSpan.SpanFull],
    elementProps: { placeholder: 'Enter email addresses, separated by comma' },
    validation: { validate: emailListValidator },
  },
  weeklyAccessReport: {
    element: FormInputTypes.TextAreaField,
    label: 'Weekly Access Report',
    layout: [GridColSpan.SpanFull],
    elementProps: { placeholder: 'Enter email addresses, separated by comma' },
    validation: { validate: emailListValidator },
  },
  siteSafetyLog: {
    element: FormInputTypes.TextAreaField,
    label: 'Site Safety Log',
    layout: [GridColSpan.SpanFull],
    elementProps: { placeholder: 'Enter email addresses, separated by comma' },
    validation: { validate: emailListValidator },
  },
  safetyObservationLog: {
    element: FormInputTypes.TextAreaField,
    label: 'Safety Observation Log',
    layout: [GridColSpan.SpanFull],
    elementProps: { placeholder: 'Enter email addresses, separated by comma' },
    validation: { validate: emailListValidator },
  },
  reportingTimeSlotHour: {
    element: FormInputTypes.Field,
    label: 'Send worker access report at time of day',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
    elementProps: { fieldType: 'number', innerRightLabel: 'Hours' },
  },
};

const EMAILS_SEPARATOR = ',';

export const getNotificationsSectionDefaultValues = (
  jobsite: Jobsite,
): EditJobsiteConfigurationFormData['notifications'] => {
  const {
    deviceStatusChangeEmails,
    accessChangeEmails,
    escortChangeEmails,
    assignmentChangeEmails,
    rejectedSwipeEmails,
    dailyAccessReportEmails,
    weeklyAccessReportEmails,
    siteSafetyLogEmails,
    safetyObservationLogEmails,
    reportingTimeSlot,
  } = jobsite ?? {};

  const getEmailsText = (emails: string[]): string => {
    if (!emails || !emails.length) return null;
    return ensureNonEmptyItems(emails)?.join(`${EMAILS_SEPARATOR} `);
  };
  const reportingTimeSlotHour =
    reportingTimeSlot &&
    moment(reportingTimeSlot, 'HH:mm:ss').toDate().getHours().toString();

  return stringifyEmptyFields<
    EditJobsiteConfigurationFormData['notifications']
  >({
    hardwareStatusChange: getEmailsText(deviceStatusChangeEmails),
    bansAndSuspensions: getEmailsText(accessChangeEmails),
    escortPrivilegeChanges: getEmailsText(escortChangeEmails),
    jobsiteAssignmentChanges: getEmailsText(assignmentChangeEmails),
    rejectedSwipes: getEmailsText(rejectedSwipeEmails),
    dailyAccessReport: getEmailsText(dailyAccessReportEmails),
    weeklyAccessReport: getEmailsText(weeklyAccessReportEmails),
    siteSafetyLog: getEmailsText(siteSafetyLogEmails),
    safetyObservationLog: getEmailsText(safetyObservationLogEmails),
    reportingTimeSlotHour,
  });
};

type NotificationSectionUpdateInput = Required<
  Pick<
    JobsiteUpdateInput,
    | 'deviceStatusChangeEmails'
    | 'accessChangeEmails'
    | 'escortChangeEmails'
    | 'assignmentChangeEmails'
    | 'rejectedSwipeEmails'
    | 'dailyAccessReportEmails'
    | 'weeklyAccessReportEmails'
    | 'siteSafetyLogEmails'
    | 'safetyObservationLogEmails'
    | 'reportingTimeSlot'
  >
>;

export const getNotificationsSectionUpdateInput = (
  notifications: EditJobsiteConfigurationFormData['notifications'],
  dirtyFields: DeepMap<EditJobsiteConfigurationFormData['notifications'], true>,
): NotificationSectionUpdateInput => {
  const getUpdateInputValue = getUpdateInputValueFunction(
    notifications,
    dirtyFields,
  );

  const getEmails = (text: string): string[] =>
    text === null || text === ''
      ? null
      : text?.split(EMAILS_SEPARATOR).map((email) => email.trim());
  const reportingTimeSlotHourValue = getUpdateInputValue(
    'reportingTimeSlotHour',
  );
  const reportingTimeSlot =
    reportingTimeSlotHourValue &&
    moment(reportingTimeSlotHourValue, 'HH').format('HH:mm:ss');

  return {
    accessChangeEmails: getEmails(getUpdateInputValue('bansAndSuspensions')),
    deviceStatusChangeEmails: getEmails(
      getUpdateInputValue('hardwareStatusChange'),
    ),
    escortChangeEmails: getEmails(
      getUpdateInputValue('escortPrivilegeChanges'),
    ),
    assignmentChangeEmails: getEmails(
      getUpdateInputValue('jobsiteAssignmentChanges'),
    ),
    rejectedSwipeEmails: getEmails(getUpdateInputValue('rejectedSwipes')),
    dailyAccessReportEmails: getEmails(
      getUpdateInputValue('dailyAccessReport'),
    ),
    weeklyAccessReportEmails: getEmails(
      getUpdateInputValue('weeklyAccessReport'),
    ),
    siteSafetyLogEmails: getEmails(getUpdateInputValue('siteSafetyLog')),
    safetyObservationLogEmails: getEmails(
      getUpdateInputValue('safetyObservationLog'),
    ),
    reportingTimeSlot,
  };
};
